var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/nodes/image.ts
import { XLightbox } from "@adornis/adornis-components/x-lightbox.js";
import { getByID } from "@adornis/baseql/operations/mongo.js";
import "@adornis/buildify/client/components/x-buildify-aspect-ratio-picker.js";
import "@adornis/buildify/client/components/x-buildify-border-radius-picker.js";
import "@adornis/buildify/client/components/x-buildify-file-selection.js";
import "@adornis/buildify/client/components/x-buildify-spacing-picker.js";
import { BuildifyFile, BuildifyImage } from "@adornis/buildify/db/BuildifyFile.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flex";
import "@adornis/chemistry/elements/components/x-icon";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import "@adornis/file-utils/client/x-image";
import "@adornis/file-utils/db/files.js";
import "@adornis/forms/x-checkbox";
import "@adornis/forms/x-input";
import "@adornis/popover/x-dropdown-selection";
import { goTo } from "@adornis/router/client/open-href.js";
import { html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { IMAGE_KEY, IMAGE_SCHEMA } from "../../schema/nodes/image.js";
import { BuildifyLitElement } from "../BuildifyLitElement.js";
import { runInsertNode } from "../helper/runInsertNode.js";
import { MenuItemGroup } from "../types.js";
import { isNodeSelection } from "../util.js";
var editor = ({ content, contentController, controllerBaseKeyPath, host }) => {
  return html`
    <x-flex space="md">
      <x-input ${contentController.field(...controllerBaseKeyPath, "href")} placeholder="Link"></x-input>
      <x-checkbox
        .label=${"Link in neuem Fenster \xF6ffnen?"}
        .value=${content.target === "_blank"}
        @value-picked=${(e) => {
    if (e.detail.value) {
      content.setValue("target", "_blank", contentController.size);
    } else {
      content.setValue("target", "_self", contentController.size);
    }
  }}
      ></x-checkbox>
      <x-buildify-spacing-picker
        ${contentController.field(...controllerBaseKeyPath, "width")}
        placeholder="Breite"
      ></x-buildify-spacing-picker>
      <x-buildify-spacing-picker
        ${contentController.field(...controllerBaseKeyPath, "height")}
        placeholder="Höhe"
      ></x-buildify-spacing-picker>
      <x-infobox>
        Wenn ein Seitenverhältnis angegeben wird, sollte nur mit einer Größe von Breite oder Höhe gearbeitet werden.
      </x-infobox>
      <x-buildify-aspect-ratio-picker
        ${contentController.field(...controllerBaseKeyPath, "aspectRatio")}
      ></x-buildify-aspect-ratio-picker>
      <x-buildify-border-radius-picker
        select
        ${contentController.field(...controllerBaseKeyPath, "borderRadius")}
        placeholder="Radius der Ecken"
      ></x-buildify-border-radius-picker>
      <x-dropdown-selection
        .selectables=${["cover", "contain", "width"]}
        placeholder="Anzeige-Modus"
        ${contentController.field(...controllerBaseKeyPath, "viewMode")}
        .renderString=${(viewMode) => {
    switch (viewMode) {
      case "cover":
        return "Platz ausf\xFCllend";
      case "contain":
        return "Vollst\xE4ndiges Bild in verf\xFCgbarem Platz";
      case "width":
        return "Vollst\xE4ndiges Bild auf voller Breite";
      default:
        return "";
    }
  }}
      ></x-dropdown-selection>
      <x-checkbox
        ${contentController.field(...controllerBaseKeyPath, "viewDownloadButton")}
        .label=${"Download Button anzeigen"}
      ></x-checkbox>
      <x-buildify-file-selection
        image
        ${contentController.field(...controllerBaseKeyPath, "fileID")}
      ></x-buildify-file-selection>
    </x-flex>
  `;
};
var image = () => ({
  node: {
    name: IMAGE_KEY,
    schema: IMAGE_SCHEMA()
  },
  menuItems: [
    {
      group: MenuItemGroup.MEDIA,
      label: "Bild",
      icon: "image",
      run: (view) => runInsertNode(IMAGE_KEY)(view.state, view),
      shouldVisualize: (view) => {
        return !isNodeSelection(view.state.selection);
      }
    }
  ],
  editor
});
var NodeImage = class extends BuildifyLitElement {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const content = this._data.value;
      if (!content)
        return;
      if (!content.href) {
        if (!content.fileID)
          return;
        const file = await getByID(BuildifyImage, content.fileID)(BuildifyImage.allFields);
        if (!file)
          return;
        XLightbox.showPopup({
          backgroundOpacity: 0.8,
          props: { files: [file], startingIndex: 0, xToClose: true }
        });
      } else {
        goTo(content.href, { target: content.target ?? "_self" });
      }
    });
  }
  // @ts-expect-error xComponents destroys it
  content({ content, mode }) {
    return xComponents(
      html`
        <x-image
          .alt=${content?.altTag}
          disabled
          mode=${content?.viewMode ?? this.defaultMode}
          .fileEntity=${this.fileClass}
          .value=${content?.fileID}
          ${css({
        width: "100%",
        height: content?.viewMode !== "width" ? "100%" : "unset",
        aspectRatio: content?.viewMode !== "width" ? content?.aspectRatio ?? "" : "unset",
        ...content?.borderRadius ? { borderRadius: content.borderRadius, overflow: "hidden" } : {},
        cursor: content?.href ? "pointer" : ""
      })}
        ></x-image>
        ${content?.viewDownloadButton && content.fileID ? this.__downloadButton(content.fileID) : nothing}
      `
    );
  }
  __downloadButton(fileID) {
    return html`
      <x-icon
        ${css({
      position: "absolute",
      right: "16px",
      bottom: "16px",
      zIndex: "9",
      background: this.colors.accent,
      color: "#fff",
      cursor: "pointer",
      padding: "4px",
      borderRadius: this.sizes.borderRadius
    })}
        @click=${() => {
      this._onDownload(fileID);
    }}
      >
        download
      </x-icon>
    `;
  }
  get defaultMode() {
    return "cover";
  }
  get fileClass() {
    return BuildifyFile;
  }
  _onDownload(fileID) {
    goTo(this.fileClass.getDownloadLink(fileID), { target: "_blank" });
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          width: this._constructedData.value?.width ?? "",
          height: this._constructedData.value?.height ?? "",
          transition: "200ms",
          "&:hover": {
            transform: this._data.value?.href ? "scale(1.05)" : ""
          }
        }
      }
    ];
  }
};
NodeImage = __decorateClass([
  customElement("node-image")
], NodeImage);
export {
  NodeImage,
  editor,
  image
};
